import { useState, useEffect } from "react";
import { Layout } from "src/vibe-code/rcm-v2/components/Layout";
import {
  exportDealersToCSV,
  exportEmployees,
} from "src/vibe-code/rcm-v2/lib/csvExport";
import { DealersTable } from "src/vibe-code/rcm-v2/components/DealersTable";
import { EmployeesTable } from "src/vibe-code/rcm-v2/components/EmployeesTable";
import { DealerProfile } from "src/vibe-code/rcm-v2/components/DealerProfile";
import { EmployeeProfile } from "src/vibe-code/rcm-v2/components/EmployeeProfile";
import { DealerForm } from "src/vibe-code/rcm-v2/components/forms/DealerForm";
import { EmployeeForm } from "src/vibe-code/rcm-v2/components/forms/EmployeeForm";
import { useDealers, useEmployees } from "src/vibe-code/rcm-api";
import { useApiClient, useSafeApiCall } from "src/hooks/api";
import type { Dealer, Employee } from "src/vibe-code/rcm-v2/types";

type View =
  | "dealers"
  | "employees"
  | "dealer-profile"
  | "employee-profile"
  | "dealer-form"
  | "employee-form";

const Index = () => {
  const { dealers, createDealer, updateDealer } = useDealers();
  const { employees, createEmployee, updateEmployee, deleteEmployee } =
    useEmployees();
  const { fetchFromApi } = useApiClient();
  const safeApiCall = useSafeApiCall();
  const [activeTab, setActiveTab] = useState<"dealers" | "employees">(
    "dealers",
  );
  const [currentView, setCurrentView] = useState<View>("dealers");
  const [selectedDealer, setSelectedDealer] = useState<Dealer | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(
    null,
  );
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (selectedDealer) {
      const refreshedSelectedDealer = dealers.filter(
        (obj) => obj.id === selectedDealer.id,
      );
      setSelectedDealer(refreshedSelectedDealer[0]);
    }
  }, [dealers]);

  useEffect(() => {
    if (selectedEmployee) {
      const refreshedSelectedEmployee = employees.filter(
        (obj) => obj.id === selectedEmployee.id,
      );
      setSelectedEmployee(refreshedSelectedEmployee[0]);
    }
  }, [employees]);

  const filteredDealers = dealers.filter((dealer) =>
    dealer.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const filteredEmployees = employees.filter(
    (employee) =>
      `${employee.first_name} ${employee.last_name}`
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      employee.dealer.name?.toLowerCase().includes(searchQuery.toLowerCase()),
  );
  const handleTabChange = (tab: "dealers" | "employees") => {
    setActiveTab(tab);
    setCurrentView(tab);
    setSearchQuery("");
  };

  const handleDealerClick = (dealer: Dealer) => {
    setSelectedDealer(dealer);
    setCurrentView("dealer-profile");
  };

  const handleEmployeeClick = (employee: Employee) => {
    setSelectedEmployee(employee);
    setCurrentView("employee-profile");
  };

  const handleBack = () => {
    if (currentView === "dealer-profile") {
      setCurrentView("dealers");
      setActiveTab("dealers");
    } else if (currentView === "employee-profile") {
      setCurrentView("employees");
      setActiveTab("employees");
    }
    setSelectedDealer(null);
    setSelectedEmployee(null);
  };

  const handleDealerClickFromEmployee = (dealerId: string) => {
    const dealer = dealers.find((d) => d.id === dealerId);
    if (dealer) {
      setSelectedDealer(dealer);
      setCurrentView("dealer-profile");
    }
  };

  const handleAddClick = () => {
    if (activeTab === "dealers") {
      setCurrentView("dealer-form");
    } else {
      setCurrentView("employee-form");
    }
  };

  const handleExportClick = async () => {
    try {
      if (activeTab === "dealers") {
        await exportDealersToCSV(fetchFromApi, safeApiCall);
      } else {
        await exportEmployees(fetchFromApi, safeApiCall);
      }
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  const handleAddEmployee = () => {
    setCurrentView("employee-form");
  };

  const handleDealerUpdate = (data: any) => {
    updateDealer(data);
  };

  const handleEmployeeDelete = (employeeId: string) => {
    deleteEmployee(employeeId);
    setCurrentView("employees");
  };

  const handleEmployeeUpdate = (updatedEmployee: Employee) => {
    updateEmployee(updatedEmployee);
  };

  const handleFormSave = (data: any) => {
    if (currentView === "dealer-form") {
      createDealer(data);
    } else if (currentView === "employee-form") {
      createEmployee(data);
    }
    setCurrentView(activeTab);
  };

  const handleFormCancel = () => {
    setCurrentView(activeTab);
  };

  const renderContent = () => {
    switch (currentView) {
      case "dealers":
        return (
          <DealersTable
            dealers={filteredDealers}
            employees={employees}
            onDealerClick={handleDealerClick}
            onEmployeeClick={handleEmployeeClick}
          />
        );
      case "employees":
        return (
          <EmployeesTable
            employees={filteredEmployees}
            onEmployeeClick={handleEmployeeClick}
          />
        );
      case "dealer-profile":
        return selectedDealer ? (
          <DealerProfile
            dealer={selectedDealer}
            employees={employees}
            onBack={handleBack}
            onEmployeeClick={handleEmployeeClick}
            onAddEmployee={handleAddEmployee}
            onUpdate={handleDealerUpdate}
          />
        ) : null;
      case "employee-profile":
        return selectedEmployee ? (
          <EmployeeProfile
            employee={selectedEmployee}
            dealers={dealers}
            onBack={handleBack}
            onDealerClick={handleDealerClickFromEmployee}
            onUpdate={handleEmployeeUpdate}
            onDelete={handleEmployeeDelete}
          />
        ) : null;
      case "dealer-form":
        return (
          <DealerForm onSave={handleFormSave} onCancel={handleFormCancel} />
        );
      case "employee-form":
        return (
          <EmployeeForm
            dealers={dealers}
            selectedDealerId={selectedDealer?.id}
            onSave={handleFormSave}
            onCancel={handleFormCancel}
          />
        );
      default:
        return null;
    }
  };

  // Don't show search and filters on profile and form pages
  const showHeader = currentView === "dealers" || currentView === "employees";

  if (!showHeader) {
    return renderContent();
  }

  return (
    <Layout
      activeTab={activeTab}
      onTabChange={handleTabChange}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      onAddClick={handleAddClick}
      onExportClick={handleExportClick}
      dealerCount={dealers.length}
      employeeCount={employees.length}
    >
      {renderContent()}
    </Layout>
  );
};

export default Index;
